import { acceptHMRUpdate, defineStore } from 'pinia'
import { keyBy } from 'lodash-es'

import {
  NO_ALLOCATION_TAG,
  NO_ALLOCATION_TAG_ID,
  NO_CATEGORY_TAG,
  NO_CATEGORY_TAG_ID,
  NO_SEGMENT_TAG,
  NO_SEGMENT_TAG_ID,
  TagType,
} from '@/constants/tags'
import DuplicateError from '@/errors/DuplicateError'
import { HttpError } from '@/helpers/errors'

import AccountTag from '@/services/account/tag'
import { useUserStore } from './user'

import type { RequestOptions } from '@/services/api.service'
import type { TagModelValue } from '@/types/components/tags'
import type { Tag, TagTypeValue, TagUpdate } from '@/types/services/tags'

export interface AssignTagsParams {
  accountId: string
  tags: TagModelValue[]
}

export interface TagsMap {
  [key: string]: Tag
}

export interface EntityTagsState {
  loading: boolean
  tags: TagsMap
}

export interface GeneralState {
  tagsMap: TagsMap
  tagsLoading: boolean
  // entity tagsMap holds; null until loaded, so "no tags" ≠ "not loaded yet".
  tagsEntityId: string | null
  tagsByEntity: {
    [entityId: string]: EntityTagsState
  }
}

function getTagsArray(tagsMap: TagsMap): Tag[] {
  return Object.values(tagsMap)
}

function findTagById(tagsMap: TagsMap, tagId: string): Tag | null {
  const extendedMap = {
    ...tagsMap,
    [NO_CATEGORY_TAG_ID]: NO_CATEGORY_TAG,
    [NO_SEGMENT_TAG_ID]: NO_SEGMENT_TAG,
    [NO_ALLOCATION_TAG_ID]: NO_ALLOCATION_TAG,
  }
  return extendedMap[tagId] ?? null
}

function filterTagsByType(tagsMap: TagsMap, type: TagTypeValue): Tag[] {
  return getTagsArray(tagsMap)
    .filter((tag) => tag.type === type)
    .sort((a, b) => a.name.localeCompare(b.name))
}

function findTagsByIds(tagsMap: TagsMap, ids: string[]): Tag[] {
  return ids
    .map((id) => findTagById(tagsMap, id))
    .filter((tag): tag is Tag => tag !== null)
    .sort((a, b) => a.name.localeCompare(b.name))
}

export const useSettingsGeneralStore = defineStore('settingsGeneral', {
  state(): GeneralState {
    return {
      tagsMap: {},
      tagsLoading: false,
      tagsEntityId: null,
      tagsByEntity: {},
    }
  },

  actions: {
    async getTags() {
      const userStore = useUserStore()
      const entityId = userStore.entityOrFail.id
      this.tagsLoading = true

      try {
        const response = await AccountTag.getTags(entityId)
        this.tagsMap = keyBy(response.data, 'id')
        this.tagsEntityId = entityId
        this.tagsLoading = false
      } catch (err) {
        const httpError = new HttpError(err)
        if (httpError.isCanceled) {
          return
        }
        this.tagsLoading = false
        throw httpError
      }
    },

    async getTagsForEntity(entityId: string, options?: RequestOptions) {
      if (this.tagsByEntity[entityId]?.loading) {
        return
      }

      this.tagsByEntity[entityId] = {
        loading: true,
        tags: this.tagsByEntity[entityId]?.tags ?? {},
      }

      try {
        const response = await AccountTag.getTagsForEntity(entityId, options)
        this.tagsByEntity[entityId] = {
          loading: false,
          tags: keyBy(response.data.data, 'id'),
        }
      } catch (err) {
        delete this.tagsByEntity[entityId]
        throw new HttpError(err)
      }
    },

    async createTag(tag: Omit<Tag, 'entity_id'>) {
      const userStore = useUserStore()
      try {
        const response = await AccountTag.createTag(
          userStore.entityOrFail.id,
          tag
        )
        return response.data
      } catch (err: any) {
        if (err.response?.status == 409) {
          throw new DuplicateError(err.response.data.detail)
        }
        throw new HttpError(err)
      }
    },

    async updateTag(tag: TagUpdate) {
      try {
        const response = await AccountTag.updateTag(tag)
        return response.data
      } catch (err: any) {
        if (err.response?.status == 409) {
          throw new DuplicateError(err.response.data.detail)
        }
        throw new HttpError(err)
      }
    },

    async deleteTag(tagId: string) {
      try {
        await AccountTag.deleteTag(tagId)
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async mergeTags(sourceTagId: string, targetTagId: string) {
      try {
        const response = await AccountTag.mergeTags(sourceTagId, targetTagId)
        return response.data.data
      } catch (err) {
        throw new HttpError(err)
      }
    },

    async assignAccountTags(params: AssignTagsParams) {
      const userStore = useUserStore()
      try {
        const response = await AccountTag.assignAccountTags(
          params.accountId,
          params.tags
        )
        const assignedTags: Tag[] = response.data.data.map((tag) => ({
          id: tag.tag_id,
          name: tag.tag_name,
          entity_id: userStore.entityOrFail.id,
          type: TagType.TAG,
        }))

        params.tags.forEach((tag) => {
          if (!tag.id) {
            const newTag = assignedTags.find(
              (assignedTag) => assignedTag.name === tag.name
            )
            if (newTag) {
              newTag.count = 1
              this.addTagToMap(newTag)
            }
          }
        })
      } catch (err) {
        throw new HttpError(err)
      }
    },

    updateTagInMap(tag: Tag) {
      if (tag.id) {
        this.tagsMap[tag.id] = tag
      }
    },

    addTagToMap(newTag: Tag) {
      if (newTag.id) {
        this.tagsMap[newTag.id] = newTag
      }
    },

    deleteTagFromMap(id: string) {
      delete this.tagsMap[id]
    },
  },

  getters: {
    // Global tags (admin context)
    tags(state) {
      return getTagsArray(state.tagsMap)
    },

    tagsByType(state) {
      return (type: TagTypeValue): Tag[] => {
        return filterTagsByType(state.tagsMap, type)
      }
    },

    accountTags(state): Tag[] {
      return filterTagsByType(state.tagsMap, TagType.TAG)
    },

    categoryTags(state): Tag[] {
      return filterTagsByType(state.tagsMap, TagType.CATEGORY_TAG)
    },

    segmentTags(state): Tag[] {
      return filterTagsByType(state.tagsMap, TagType.SEGMENT_TAG)
    },

    /** The entity's Allocation vocabulary (UI "Allocation"). */
    allocationTags(state): Tag[] {
      return filterTagsByType(state.tagsMap, TagType.ALLOCATION_TAG)
    },

    /** Label for an allocation id: a tag id, or the 'Other' bucket.
     *
     * Allocations are tag UUIDs now, so an unresolved one must not fall back
     * to the id -- it renders as a UUID in filters and charts whenever the
     * vocabulary has not loaded (an entity switch refetches it).
     */
    allocationNameById(state) {
      return (allocationId: string): string => {
        return findTagById(state.tagsMap, allocationId)?.name ?? '-'
      }
    },

    openPositionTags(state): Tag[] {
      return filterTagsByType(state.tagsMap, TagType.OPEN_POSITION_TAG)
    },

    tagById(state) {
      return (id: string): Tag | null => {
        const tag = findTagById(state.tagsMap, id)
        if (tag) return tag

        for (const entityState of Object.values(state.tagsByEntity)) {
          const entityTag = findTagById(entityState.tags, id)
          if (entityTag) return entityTag
        }
        return null
      }
    },

    tagsByIds(state) {
      return (ids: string[]): Tag[] => {
        return findTagsByIds(state.tagsMap, ids)
      }
    },

    isEntityTagsLoaded(state) {
      return (entityId: string): boolean => {
        const entityState = state.tagsByEntity[entityId]
        return entityState !== undefined && !entityState.loading
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useSettingsGeneralStore, import.meta.hot)
  )
}
